/**
 * Argus, on somebody else's chart.
 *
 * Registers one overlay provider with the shell (`state/chart-overlays.ts`) and
 * answers with shapes. The provider is the only way Argus reaches a chart: it
 * hands over geometry, the renderer draws geometry, and neither knows what the
 * other is for. Delete `features/argus/` and the registry is simply empty.
 *
 * **Nothing here is computed.** A mark is a number somebody stored - a rule's
 * threshold - and a rule whose reference needs a watcher to work out does not
 * appear in the list at all. Explorer never evaluates (`docs/plans/argus.md` rule 3), and
 * a band that was guessed at is worse than no band, because a line on a chart
 * reads as a fact (rule 9).
 *
 * **The provider never fetches.** It is called during a draw, on a chart that
 * may be one of several in a grid. The marks are loaded once when the lens goes
 * on, kept in an atom, and answered from memory; until they arrive it answers
 * with nothing, which is also the honest thing to draw when you do not know.
 *
 * Two kinds of mark are drawn, and the difference is deliberate:
 *
 * * **Deployed** rules, which everybody with Argus sees.
 * * **Your own drafts**, which only you see. A threshold is a number somebody
 *   has to choose, and choosing it without ever seeing it against the data is
 *   how a rule ends up wrong. The server decides this; the label says which.
 */

import { listArgusOverlays } from '../../api/endpoints.ts';
import type { ArgusMark } from '../../api/types.ts';
import { Atom } from '../../state/atom.ts';
import {
  registerOverlayProvider,
  type OverlayContext,
  type OverlayShape,
} from '../../state/chart-overlays.ts';
import { argusOn, hasArgus } from './argus-lens.ts';

/** The Argus hue, resolved by the renderer from the same token the CSS uses. */
export const ARGUS_OVERLAY_COLOR = 'var(--argus)';

/** What the server last told us a chart may draw. Empty until it has. */
export const marks = new Atom<ArgusMark[]>([]);

/**
 * Which plot types can carry an Argus shape honestly.
 *
 * One type at a time (`todos/argus-roadmap.md`, track C): a chart that cannot say
 * something true carries nothing rather than something decorative.
 */
const DRAWN_ON = new Set<string>(['line']);

let loading: Promise<void> | null = null;
let loadedFor: string | null = null;

/**
 * Load the marks, once.
 *
 * Called when the lens goes on and after a rule is written or deployed - the
 * two moments the answer can have changed. A failure leaves the list as it was
 * and is not retried on a timer: a chart that quietly stops showing a threshold
 * is a smaller problem than one that keeps asking a server that is not there.
 */
export async function loadArgusMarks(reason = 'lens'): Promise<void> {
  if (!hasArgus()) return;
  if (loading && loadedFor === reason) return loading;
  loadedFor = reason;
  loading = (async () => {
    try {
      const answer = await listArgusOverlays();
      marks.set(answer.marks ?? []);
    } catch {
      // Argus is a guest on these charts. It does not get to raise an error
      // over somebody else's plot.
    } finally {
      loading = null;
    }
  })();
  return loading;
}

/** Forget what was loaded, so the next ask goes to the server. */
export function invalidateArgusMarks(): void {
  loadedFor = null;
  void loadArgusMarks(`refresh-${Date.now()}`);
}

/**
 * Shapes for one chart.
 *
 * Exported so it can be tested without a canvas, and so the next chart type is
 * a change here rather than a second provider.
 */
export function argusShapesFor(context: OverlayContext): OverlayShape[] {
  // The lens is what the person asked for. Off, Argus is not on their chart -
  // even though the rules behind it are still running (`docs/plans/argus.md` rule 8).
  if (!argusOn()) return [];
  if (!DRAWN_ON.has(context.plotType)) return [];
  if (!context.entityIds.length) return [];

  const entities = new Set(context.entityIds);
  const shapes: OverlayShape[] = [];

  for (const mark of marks.value) {
    if (!entities.has(mark.entity)) continue;
    // Which axis the series is drawn on decides which scale the line belongs
    // to. A rule about a series this chart is not drawing says nothing here -
    // it is not wrong, it is simply about something else.
    const onLeft = context.left.includes(mark.series);
    const onRight = !onLeft && context.right.includes(mark.series);
    if (!onLeft && !onRight) continue;

    // One line per ship is one line too many when the same rule covers several
    // and they share a scale - the value is the value. Named by its rule, so a
    // person can tell which one they are looking at.
    shapes.push({
      kind: 'line',
      value: mark.value,
      scale: onLeft ? 'y' : 'y2',
      // The draft marker is not decoration: it says why nobody else can see
      // this line, which is the question it would otherwise raise.
      label: mark.status === 'draft' ? `${mark.name} (draft)` : mark.name,
      color: ARGUS_OVERLAY_COLOR,
      style: 'dashed',
    });
  }

  return shapes;
}

registerOverlayProvider({
  id: 'argus',
  shapes: argusShapesFor,
});
